import { CheckCircle2, ExternalLink, Loader2, LogOut, RefreshCw, Store } from "lucide-react";
import React, { useEffect, useState } from "react";
import { API_BASE } from "../../../../consts";
import authService from "../../../../services/AuthService";

/**
 * Shows whether the current user has a linked Walmart Seller account,
 * and if not, lets them start the OAuth connect flow.
 *
 * Reads status from GET /api/profile -> walmart_linked_account
 * (populated by your backend once the OAuth callback completes and
 * calls PUT /api/profile/linked-accounts).
 */
const WalmartLoginButton = ({ onStatusChange }) => {
  const [status, setStatus] = useState("loading"); // loading | connected | disconnected | error
  const [account, setAccount] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadStatus = async () => {
    setStatus("loading");
    try {
      const res = await authService.authenticatedFetch(`${API_BASE}/api/profile`);
      if (!res.ok) throw new Error("Failed to load profile");
      const data = await res.json();
      const linked = data.walmart_linked_account;

      const isOAuthLinked = !!(linked && linked.seller_id && linked.refresh_token);

      if (isOAuthLinked) {
        setAccount(linked);
        setStatus("connected");
        onStatusChange?.(true, linked);
      } else {
        setAccount(null);
        setStatus("disconnected");
        onStatusChange?.(false, null);
      }
    } catch (err) {
      setStatus("error");
    }
  };

  useEffect(() => {
    loadStatus();

    // If we just landed back here from Walmart, clean the URL and re-check.
    const params = new URLSearchParams(window.location.search);
    if (params.get("walmart")) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete("walmart");
      window.history.replaceState({}, "", clean.toString());
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      // Ask our backend for a one-time connect URL tied to this logged-in user.
      const res = await authService.authenticatedFetch(
        `${API_BASE}/api/walmart/connect-url`,
        { method: "POST" }
      );
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url; // full browser navigation to Walmart
      } else {
        setConnecting(false);
      }
    } catch (err) {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Walmart Seller account?")) return;
    setDisconnecting(true);
    try {
      await authService.authenticatedFetch(`${API_BASE}/api/walmart/disconnect`, {
        method: "POST",
      });
      await loadStatus();
    } catch (err) {
      // status just won't update; user can retry
    } finally {
      setDisconnecting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-400 text-sm">
        <Loader2 size={16} className="animate-spin" /> Checking Walmart connection…
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50">
        <p className="text-sm font-semibold text-red-600">
          Couldn't check Walmart connection status.
        </p>
        <button
          onClick={loadStatus}
          className="text-xs font-bold text-red-600 hover:text-red-700 flex items-center gap-1"
        >
          <RefreshCw size={13} /> Retry
        </button>
      </div>
    );
  }

  if (status === "connected") {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-green-200 bg-green-50">
        <div className="w-9 h-9 rounded-lg bg-white border border-green-200 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 size={18} className="text-green-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-green-800">Walmart Connected</p>
          <p className="text-xs text-green-600 truncate">
            {account?.seller_id
              ? `Seller ID: ${account.seller_id}`
              : "Your Walmart Seller account is linked"}
          </p>
        </div>
        <button
          onClick={loadStatus}
          title="Refresh status"
          className="p-2 text-green-500 hover:text-green-700 hover:bg-green-100 rounded-lg transition"
        >
          <RefreshCw size={15} />
        </button>
        <button
          onClick={handleDisconnect}
          disabled={disconnecting}
          title="Disconnect Walmart"
          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
        >
          {disconnecting ? (
            <Loader2 size={15} className="animate-spin" />
          ) : (
            <LogOut size={15} />
          )}
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={handleConnect}
      disabled={connecting}
      className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-sm hover:opacity-90 transition shadow-lg disabled:opacity-60"
    >
      {connecting ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <Store size={16} />
      )}
      {connecting ? "Redirecting to Walmart…" : "Connect Walmart Seller Account"}
      {!connecting && <ExternalLink size={13} className="opacity-70" />}
    </button>
  );
};

export default WalmartLoginButton;